import { getRunnerData } from '../api/campaignData';
import type { StepKind } from './steps';

/**
 * DB-authoring seam for the *in-step section headers* — the `Block`/`SessionPresentation` headings and
 * leads each step renders inside its content card (distinct from the step's own title/subtitle, which
 * `resolveStepDef` already covers). These were the last hard-coded pieces of runner copy the admin
 * could not touch.
 *
 * The bundled step components keep their exact literal defaults inline (so an un-authored campaign — and
 * the standalone demo — renders byte-identically); this module only supplies:
 *   - `sectionHeadings(kind)`: the campaign's DB overrides for a step, read live from the portal context
 *     (`RunnerDataContext.stepContent[].sectionHeadings`, from the step's `metadata.runner.sectionHeadings`).
 *     A component reads `sectionHeadings('deepdive').main?.title || '<its literal default>'`.
 *   - `STEP_SECTION_HEADINGS`: the registry the admin form iterates to know which section fields a step
 *     kind exposes, with each section's default copy shown as the field placeholder.
 *
 * Mirrors `resolveStepDef` / `resolveStakeholders`: "DB when present, bundled otherwise."
 */

export interface SectionHeadingOverride {
  title?: string;
  lead?: string;
}

export interface SectionHeadingDef {
  /** Stable id keyed into `metadata.runner.sectionHeadings`. */
  id: string;
  /** Admin-facing label for the field group. */
  label: string;
  /** Default title copy, shown as the admin placeholder. */
  title: string;
  /** Default lead copy, shown as the admin placeholder. Omitted for title-only sections. */
  lead?: string;
  /** True when this section renders no lead (title-only), so the admin shows no lead field. */
  titleOnly?: boolean;
  /** True when the title is derived by the runner (e.g. from a deck label), so blank = derived. */
  titleDerived?: boolean;
}

/**
 * The section headers each frontier step renders, in on-screen order. `id`s are stable — they key the
 * stored overrides and must not change once authored. Copy here mirrors the components' inline defaults
 * and is used only as the admin placeholder; the components remain the source of the rendered fallback.
 *
 * Propose's "Your conversation guide" heading is intentionally absent: it is already authorable through
 * the dedicated `guideTitle`/`guideBody` fields.
 */
export const STEP_SECTION_HEADINGS: Partial<Record<StepKind, SectionHeadingDef[]>> = {
  profiling: [
    {
      id: 'connect',
      label: 'Connect-your-tools heading',
      title: 'Connect your tools',
      titleOnly: true,
    },
  ],
  prepare: [
    {
      id: 'roster',
      label: 'Stakeholder roster',
      title: 'Get the right people in the room',
      lead: 'During the proposal meeting, please agree with your champion on which stakeholders should join the main workshop, and be ready to explain why each one matters.',
    },
  ],
  propose: [
    {
      id: 'discovery',
      label: 'Recommended discovery questions',
      title: 'Recommended Discovery Questions for your Initial Conversation',
      lead: 'Use these as a conversation starter, not a script — the strongest sessions are an open, collaborative discussion with your AE and portfolio engineer in the room. Capture what your champion says as you go.',
    },
  ],
  workshop: [
    {
      id: 'main',
      label: 'Workshop slides',
      title: '(defaults to the deck’s label)',
      titleDerived: true,
      lead: 'Prior to the workshop, review the slides below and customize the slides to your needs based on pre-workshop data and initial customer conversations.',
    },
  ],
  deepdive: [
    {
      id: 'main',
      label: 'Deep-dive panel',
      title: 'Workshop deep-dive',
      lead: 'Use these questions as needed to help guide the conversation.  Record the answers here to help the tool provide more meaningful recommendations',
    },
  ],
  recommendation: [
    {
      id: 'gap',
      label: 'Gap analysis',
      title: 'Gap analysis',
      lead: 'Built from what you recorded in the deep-dive.',
    },
    {
      id: 'plan',
      label: 'Action plan',
      title: 'Prioritized Action Plan',
      lead: 'Edit any section in place. This is the document that gets exported.',
    },
  ],
};

/** The campaign's DB-authored section-header overrides for a step kind, or `{}` when none/standalone. */
export function sectionHeadings(kind: StepKind): Record<string, SectionHeadingOverride> {
  return (
    getRunnerData()?.stepContent?.find((c) => c.kind === kind)?.sectionHeadings ?? {}
  );
}
